import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SecretsModal.css';
import { modalInputClass } from './modalInputClass';
import ModernDatePicker from './ModernDatePicker';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formatDateForDisplay = (date) => {
  if (!DATE_VALUE_PATTERN.test(date)) return date;
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
};

const WriteOrderModal = ({
  isOpen,
  onClose,
  onSubmit,
  stock,
  futures,
  broker,
  stockPrice: initialStockPrice,
  futuresPrice: initialFuturesPrice
}) => {
  useModalSelectionGuard(isOpen);

  const [dateStr, setDateStr] = useState(getTodayISO);
  const [count, setCount] = useState('1');
  const [stockPriceStr, setStockPriceStr] = useState('');
  const [futuresPriceStr, setFuturesPriceStr] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setDateStr(getTodayISO());
    setIsDatePickerOpen(false);
    setCount('1');
    setStockPriceStr(initialStockPrice != null && !isNaN(initialStockPrice) ? String(initialStockPrice) : '');
    setFuturesPriceStr(initialFuturesPrice != null && !isNaN(initialFuturesPrice) ? String(initialFuturesPrice) : '');
    setFieldErrors({});
    setError(null);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- цены только при открытии; ↺ берёт актуальные из props

  const clearFieldError = (field) => {
    setFieldErrors((previous) => {
      if (!previous[field]) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const handleCountChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setCount(value);
    clearFieldError('count');
    if (error) setError(null);
  };

  const limitToTwoDecimals = (value) => {
    const normalized = value.replace(',', '.');
    if (normalized === '' || normalized === '.') return true;
    const parts = normalized.split('.');
    return parts.length <= 1 || parts[1].length <= 2;
  };

  const handleStockPriceChange = (e) => {
    const value = e.target.value;
    if (!/^[\d.,]*$/.test(value)) return;
    if (!limitToTwoDecimals(value)) return;
    setStockPriceStr(value);
    clearFieldError('stockPrice');
    if (error) setError(null);
  };

  const handleFuturesPriceChange = (e) => {
    const value = e.target.value;
    if (!/^[\d.,]*$/.test(value)) return;
    if (!limitToTwoDecimals(value)) return;
    setFuturesPriceStr(value);
    clearFieldError('futuresPrice');
    if (error) setError(null);
  };

  const handleCancel = useCallback(() => {
    setDateStr(getTodayISO());
    setIsDatePickerOpen(false);
    setCount('1');
    setStockPriceStr('');
    setFuturesPriceStr('');
    setFieldErrors({});
    setError(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isDatePickerOpen) {
          setIsDatePickerOpen(false);
          return;
        }
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDatePickerOpen, handleCancel]);

  useEffect(() => {
    if (!isDatePickerOpen) return undefined;

    const handlePointerDown = (e) => {
      if (!datePickerRef.current?.contains(e.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isDatePickerOpen]);

  const handleOk = () => {
    const nextFieldErrors = {};
    if (!dateStr || !DATE_VALUE_PATTERN.test(dateStr)) {
      nextFieldErrors.date = true;
    }
    const countValue = parseInt(count, 10);
    if (isNaN(countValue) || countValue <= 0) {
      nextFieldErrors.count = true;
    }
    const stockPriceValue = parseFloat(stockPriceStr.replace(',', '.'));
    const futuresPriceValue = parseFloat(futuresPriceStr.replace(',', '.'));
    if (isNaN(stockPriceValue) || stockPriceValue <= 0) {
      nextFieldErrors.stockPrice = true;
    }
    if (isNaN(futuresPriceValue) || futuresPriceValue <= 0) {
      nextFieldErrors.futuresPrice = true;
    }
    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length > 0) {
      if (nextFieldErrors.date) setError('Введите корректную дату');
      else if (nextFieldErrors.count) setError('Введите корректное количество фьючерсов');
      else if (nextFieldErrors.stockPrice) setError('Введите корректную цену акции');
      else setError('Введите корректную цену фьючерса');
      return;
    }
    onSubmit({ date: dateStr, count: countValue, stockPrice: stockPriceValue, futuresPrice: futuresPriceValue });
  };

  if (!isOpen) return null;

  const brokerLabel = broker === 'bcs' ? 'БКС' : broker === 'finam' ? 'Финам' : broker;
  const defaultCountValue = '1';
  const defaultStockPriceValue =
    initialStockPrice != null && !isNaN(initialStockPrice) ? String(initialStockPrice) : '';
  const defaultFuturesPriceValue =
    initialFuturesPrice != null && !isNaN(initialFuturesPrice) ? String(initialFuturesPrice) : '';

  const getInputClassName = (field, { clickable = false } = {}) =>
    modalInputClass({
      withActions: true,
      clickable,
      error: Boolean(fieldErrors[field]),
    });

  return (
    <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
      <div className="secrets-modal secrets-modal--calendar-popover" onClick={e => e.stopPropagation()}>
        <div className="secrets-modal-header">
          <h2>Записать сделку</h2>
          <button className="secrets-close-btn" onClick={handleCancel}>×</button>
        </div>

        <div className="secrets-form">
          <div className="form-group">
            <div className="secrets-modal-summary">
              {stock} / {futures}{' // '}{brokerLabel}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="writeOrderDate">Дата</label>
            <div className="secrets-input-with-clear write-order-date-field" ref={datePickerRef}>
              <input
                type="text"
                id="writeOrderDate"
                value={formatDateForDisplay(dateStr)}
                readOnly
                onClick={() => setIsDatePickerOpen(true)}
                className={getInputClassName('date', { clickable: true })}
                onFocus={() => setIsDatePickerOpen(true)}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => setIsDatePickerOpen(prev => !prev)}
                  className="secrets-input-action-btn"
                  aria-label="Открыть календарь"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDateStr(getTodayISO());
                    clearFieldError('date');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить дату по умолчанию"
                  disabled={dateStr === getTodayISO()}
                >
                  ↺
                </button>
              </div>
              {isDatePickerOpen && (
                <div className="write-order-date-popover">
                  <ModernDatePicker
                    value={dateStr}
                    className="modern-date-picker--light modern-date-picker--popover"
                    onChange={(date) => {
                      setDateStr(date);
                      setIsDatePickerOpen(false);
                      clearFieldError('date');
                      if (error) setError(null);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="writeOrderStockPrice">Цена акции</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="writeOrderStockPrice"
                value={stockPriceStr}
                onChange={handleStockPriceChange}
                placeholder="Введите цену"
                inputMode="decimal"
                className={getInputClassName('stockPrice')}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setStockPriceStr(defaultStockPriceValue);
                    clearFieldError('stockPrice');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить цену акции по умолчанию"
                  disabled={stockPriceStr === defaultStockPriceValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStockPriceStr('');
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить цену акции"
                  disabled={!stockPriceStr}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="writeOrderFuturesPrice">Цена фьючерса</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="writeOrderFuturesPrice"
                value={futuresPriceStr}
                onChange={handleFuturesPriceChange}
                placeholder="Введите цену"
                inputMode="decimal"
                className={getInputClassName('futuresPrice')}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setFuturesPriceStr(defaultFuturesPriceValue);
                    clearFieldError('futuresPrice');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить цену фьючерса по умолчанию"
                  disabled={futuresPriceStr === defaultFuturesPriceValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFuturesPriceStr('');
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить цену фьючерса"
                  disabled={!futuresPriceStr}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="writeOrderCount">Количество фьючерсов</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="writeOrderCount"
                value={count}
                onChange={handleCountChange}
                placeholder="Введите количество"
                inputMode="numeric"
                className={getInputClassName('count')}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setCount(defaultCountValue);
                    clearFieldError('count');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить количество фьючерсов по умолчанию"
                  disabled={count === defaultCountValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCount('');
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить количество фьючерсов"
                  disabled={!count}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="secrets-modal-buttons">
            <button type="button" className="secrets-cancel-btn" onClick={handleCancel}>
              Отмена
            </button>
            <button type="button" className="secrets-save-btn" onClick={handleOk}>
              Записать
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WriteOrderModal;
